/**
 * Micro-progression animée de la barre de déploiement (§3.3).
 *
 * Toute l'arithmétique vit dans `lib/progress.ts` (pure, testée) ; ce hook ne
 * fait que la cadencer avec `requestAnimationFrame` et publier la valeur.
 */

import type { DeploymentState } from '@portail/shared';
import { useEffect, useRef, useState } from 'react';

import { displayCeiling, nextDisplayedProgress } from '../lib/progress';

/** Écart en dessous duquel on cesse de redessiner : l'œil ne le voit pas. */
const EPSILON = 0.01;

/**
 * Rend la progression à afficher : elle rejoint `target` en douceur et,
 * lorsqu'aucun événement n'arrive, continue d'avancer très lentement dans les
 * limites de l'étape courante.
 *
 * @param jobId identifiant du job suivi. Il est **exigé**, et non facultatif :
 *              la valeur affichée ne recule jamais — c'est la garantie de
 *              monotonie de `nextDisplayedProgress` — or une reprise navigue
 *              d'un job à l'autre sans démonter le composant, la route étant la
 *              même. La barre héritait donc du 100 % de l'échec précédent et y
 *              restait collée, pendant que les segments, eux, affichaient
 *              correctement l'étape en cours.
 */
export function useAnimatedProgress(
  target: number,
  state: DeploymentState | null,
  jobId: string,
): number {
  const [displayed, setDisplayed] = useState(0);

  // Les valeurs lues par la boucle d'animation passent par des références :
  // relancer la boucle à chaque rendu la ferait bégayer.
  const targetRef = useRef(target);
  const stateRef = useRef(state);
  const valueRef = useRef(0);

  targetRef.current = target;
  stateRef.current = state;

  // Remise à zéro pendant le rendu, et non dans un effet : après le commit, la
  // barre aurait affiché la valeur de l'ancien job le temps d'une image.
  const [jobSuivi, setJobSuivi] = useState(jobId);
  if (jobSuivi !== jobId) {
    setJobSuivi(jobId);
    valueRef.current = 0;
    setDisplayed(0);
  }

  useEffect(() => {
    let frame = 0;
    let previousTime: number | null = null;

    const tick = (time: number): void => {
      const elapsedMs = previousTime === null ? 0 : time - previousTime;
      previousTime = time;

      const currentState = stateRef.current;
      if (currentState) {
        const ceiling = displayCeiling(currentState, targetRef.current);
        const next = nextDisplayedProgress({
          displayed: valueRef.current,
          target: targetRef.current,
          ceiling,
          elapsedMs,
        });

        if (next - valueRef.current > EPSILON) {
          valueRef.current = next;
          setDisplayed(next);
        } else if (next > valueRef.current) {
          // On accumule sans redessiner : sinon la dérive, très lente, serait
          // perdue image après image par le seuil ci-dessus.
          valueRef.current = next;
        }
      }

      frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return displayed;
}
